import withAuth from "@auth/withAuth";
import {
  Button,
  DatePicker,
  Flex,
  Icons,
  Input,
  Table,
} from "@components/index";
import { ClbMultiSelect, ClbPagination } from "@celebration/react";
import type { MultiSelectOptions } from "@celebration/react/ClbMultiSelect/ClbMultiSelect.types";
import { useGetAllProcess } from "@services/process/hooks";
import {
  useGetDelayManagement,
} from "@services/delayManagement/hooks";
import { useReport } from "@services/report/hooks";
import Justification from "@pages/Dashboard/fragments/DetailsGantt/fragments/Justification/Justification.fragment";
import JustificationHistory from "./fragments/JustificationHistory/JustificationHistory.fragment";
import { isDateAfterTodayOrBeforeJuneOfLastYear } from "@utils/datetime";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import styles from "./DelayManagement.module.scss";

const pageSize = 15;
enum Status {
  ONHOLD = "ONHOLD",
  DOING = "DOING",
  CONCLUDED = "CONCLUDED",
  AWAITJUSTIFICATION = "AWAITJUSTIFICATION",
}

const statusDisplayLabels: Record<Status, string> = {
  [Status.ONHOLD]: "Em Espera",
  [Status.DOING]: "Em Andamento",
  [Status.CONCLUDED]: "Concluído",
  [Status.AWAITJUSTIFICATION]: "Aguardando Justificativa",
};

const statusOptionsLegacy = [
  { id: "AwaitingJustification", label: "Aguardando Justificativa" },
  { id: "OnHold", label: "Em Espera" },
  { id: "Doing", label: "Em Andamento" },
  { id: "Concluded", label: "Concluído" },
];
const statusOptions = Object.values(Status).map((id) => ({
  id,
  label: statusDisplayLabels[id],
}));
void statusOptionsLegacy;
const yesNoOptions = [
  { id: "true", label: "Sim" },
  { id: "false", label: "Não" },
];
enum Channel {
  AS = "AS",
  CEDCMP = "CEDCMP",
  REVENDA = "REVENDA",
  TMS = "TMS",
}

const channelOptions = Object.values(Channel).map((id) => ({
  id,
  label: id,
}));

const formatDate = (value: string) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString("pt-BR") : "-";
const valueOrDash = (value?: string | null) => value || "-";
const renderTruncatedText = (value?: string | null) => {
  const text = valueOrDash(value);

  return (
    <span className={styles.truncatedCell} title={text}>
      {text}
    </span>
  );
};
const dateFromString = (value: string) => new Date(`${value}T00:00:00`);
const isAwaitingJustification = (status: string) =>
  /AWAIT|WAITINGJUSTIFICATION/i.test(status);
const formatChannels = (value?: string[]) =>
  value?.length ? (
    <div className={styles.channelTags}>
      {value.map((channel) => {
        const normalizedChannel =
          channel === "CED/CMP" ? Channel.CEDCMP : channel;

        return (
          <span className={styles.channelTag} key={normalizedChannel}>
            {normalizedChannel}
          </span>
        );
      })}
    </div>
  ) : (
    "-"
  );
const statusLabel: Record<string, string> = {
  AwaitingJustification: "Aguardando Justificativa",
  WaitingJustification: "Aguardando Justificativa",
  OnHold: "Em Espera",
  Doing: "Em Andamento",
  Concluded: "Concluído",
  ONHOLD: "Em espera",
  DOING: "Em andamento",
  AWAITJUSTIFICATION: "Aguardando justificativa",
  CONCLUDED: "Completo",
};
const statusTags: Record<
  string,
  { text: string; className: string; icon?: "warning" | "check" }
> = {
  ONHOLD: {
    text: statusDisplayLabels[Status.ONHOLD],
    className: styles.statusOnHold,
  },
  DOING: {
    text: statusDisplayLabels[Status.DOING],
    className: styles.statusDoing,
  },
  AWAITJUSTIFICATION: {
    text: statusDisplayLabels[Status.AWAITJUSTIFICATION],
    className: styles.statusAwaitingJustification,
    icon: "warning",
  },
  CONCLUDED: {
    text: statusDisplayLabels[Status.CONCLUDED],
    className: styles.statusConcluded,
    icon: "check",
  },
};

const normalizeStatus = (status: string) => {
  switch (status.toUpperCase()) {
    case "ONHOLD":
    case "ON_HOLD":
      return Status.ONHOLD;
    case "DOING":
      return Status.DOING;
    case "CONCLUDED":
    case "COMPLETED":
      return Status.CONCLUDED;
    case "AWAITJUSTIFICATION":
    case "AWAITINGJUSTIFICATION":
    case "WAITINGJUSTIFICATION":
      return Status.AWAITJUSTIFICATION;
    default:
      return status;
  }
};

const StatusCell: React.FC<{ status: string }> = ({ status }) => {
  const normalizedStatus = normalizeStatus(status);
  const tag = statusTags[normalizedStatus];

  if (!tag) return statusLabel[status] || normalizedStatus;

  return (
    <div className={`${styles.statusTag} ${tag.className}`}>
      <Icons icon={tag.icon} size="md" />
      {tag.text}
    </div>
  );
};
const optionsWithChecked = (
  options: MultiSelectOptions[],
  selected: string[]
) =>
  options.map((option) => ({
    ...option,
    checked: selected.includes(String(option.id)),
  }));

const DelayManagement: React.FC = () => {
  const { getDelayReport } = useReport();
  const queryClient = useQueryClient();
  const { data: processes = [] } = useGetAllProcess();
  const [initialDate, setInitialDate] = useState<Date>(() => new Date());
  const [finalDate, setFinalDate] = useState<Date>(() => new Date());
  const [channels, setChannels] = useState<string[]>([]);
  const [processDefinitionIds, setProcessDefinitionIds] = useState<number[]>(
    []
  );
  const [statuses, setStatuses] = useState<string[]>([]);
  const [hasOpenedIncidents, setHasOpenedIncidents] = useState<string[]>([]);
  const [incidentNumber, setIncidentNumber] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Sentinel.DelayManagement.Item[]>([]);
  const [justificationSelection, setJustificationSelection] = useState<{
    row: Sentinel.DelayManagement.Item;
    processDefinitionId: number;
  }>();
  const [historySelection, setHistorySelection] = useState<{
    row: Sentinel.DelayManagement.Item;
    processDefinitionId: number;
  }>();
  const [appliedFilters, setAppliedFilters] = useState<
    Pick<
    Sentinel.DelayManagement.Filters,
      | "initialDate"
      | "finalDate"
      | "channels"
      | "processDefinitionIds"
      | "statuses"
      | "hasOpenedIncidents"
      | "incidentNumber"
    >
  >(() => ({
    initialDate,
    finalDate,
    channels: [],
    processDefinitionIds: [],
    statuses: [],
    hasOpenedIncidents: [],
    incidentNumber: "",
  }));

  const filters: Sentinel.DelayManagement.Filters = useMemo(
    () => ({
      ...appliedFilters,
      page,
      size: pageSize,
    }),
    [appliedFilters, page]
  );
  const { data, isLoading, isFetching } = useGetDelayManagement(filters);
  const rows = data?.data || [];
  const tableRows = rows;
  const processOptions = processes.map((process) => ({
    id: process.id,
    label: process.displayName,
  }));
  const getRowProcessDefinitionId = (row: Sentinel.DelayManagement.Item) => {
    const rawProcessDefinitionId =
      row.processDefinitionId ??
      row.processId ??
      row.id ??
      processes.find((process) => process.displayName === row.processName)?.id;
    return Number(rawProcessDefinitionId);
  };

  const openJustification = (row: Sentinel.DelayManagement.Item) => {
    const processDefinitionId = getRowProcessDefinitionId(row);
    setJustificationSelection({
      row,
      processDefinitionId: Number.isFinite(processDefinitionId)
        ? processDefinitionId
        : 0,
    });
  };
  const closeJustification = () => {
    setJustificationSelection(undefined);
  };
  const openHistory = (row: Sentinel.DelayManagement.Item) => {
    const processDefinitionId = getRowProcessDefinitionId(row);

    setHistorySelection({
      row,
      processDefinitionId: Number.isFinite(processDefinitionId)
        ? processDefinitionId
        : 0,
    });
  };
  const refreshAfterJustificationSaved = () => {
    void queryClient.invalidateQueries({ queryKey: ["delay-management"] });
  };
  const rowKey = (row: Sentinel.DelayManagement.Item) =>
    `${row.dateOfDelay}-${row.processName}-${row.user || ""}`;

  const updateSelection = (row: Sentinel.DelayManagement.Item, checked: boolean) => {
    setSelected((current) =>
      checked
        ? current.some((item) => rowKey(item) === rowKey(row))
          ? current
          : [...current, row]
        : current.filter((item) => rowKey(item) !== rowKey(row))
    );
  };

  const selectAll = (checked: boolean) => {
    setSelected((current) =>
      checked
        ? [
            ...current,
            ...rows.filter(
              (row) => !current.some((item) => rowKey(item) === rowKey(row))
            ),
          ]
        : current.filter(
            (item) => !rows.some((row) => rowKey(item) === rowKey(row))
          )
    );
  };

  const exportReport = () => {
    const selectedProcessDefinitionIds = [
      ...new Set(
        selected
          .map(getRowProcessDefinitionId)
          .filter((id) => Number.isFinite(id))
      ),
    ];

    return getDelayReport(
      appliedFilters.initialDate || initialDate,
      appliedFilters.finalDate || finalDate,
      selected.length > 0
        ? selectedProcessDefinitionIds
        : appliedFilters.processDefinitionIds
    );
  };

  const consult = () => {
    setAppliedFilters({
      initialDate,
      finalDate,
      channels: [...channels],
      processDefinitionIds: [...processDefinitionIds],
      statuses: [...statuses],
      hasOpenedIncidents: [...hasOpenedIncidents],
      incidentNumber,
    });
    setPage(0);
  };

  const multi = (
    label: string,
    placeholder: string,
    options: MultiSelectOptions[],
    selectedValues: string[],
    onChange: (values: string[]) => void
  ) => (
    <div className={styles.field}>
      <ClbMultiSelect
        label={label}
        placeholder={placeholder}
        size="lg"
        options={optionsWithChecked(options, selectedValues)}
        i18n={{
          selectAllOptions: "Selecionar todos",
          labelTag: "selecionado(s)",
          noOptionsLabel: "Nenhuma opção disponível",
        }}
        onChange={(event) =>
          onChange(event.selectedOptions.map((option) => String(option.id)))
        }
      />
    </div>
  );

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <Button
          label="Extrair relatório"
          icon="download"
          onClick={exportReport}
        />
      </div>
      <div className={styles.filters}>
        <div className={`${styles.field} ${styles.dateField}`}>
          <DatePicker
            id="initialDate"
            label="Data Inicial"
            size="lg"
            value={initialDate}
            onChange={setInitialDate}
            shouldDisableDate={isDateAfterTodayOrBeforeJuneOfLastYear}
          />
        </div>
        <div className={`${styles.field} ${styles.dateField}`}>
          <DatePicker
            id="finalDate"
            label="Data Final"
            size="lg"
            value={finalDate}
            onChange={setFinalDate}
            shouldDisableDate={isDateAfterTodayOrBeforeJuneOfLastYear}
          />
        </div>
        {multi(
          "Canal",
          "Selecione o canal desejado",
          channelOptions,
          channels,
          setChannels
        )}
        {multi(
          "Processo",
          "Selecione o processo",
          processOptions,
          processDefinitionIds.map(String),
          (values) => setProcessDefinitionIds(values.map(Number))
        )}
        {multi(
          "Status",
          "Selecione o status desejado",
          statusOptions,
          statuses,
          setStatuses
        )}
        {multi(
          "Incidente Aberto?",
          "Selecione o status do incidente",
          yesNoOptions,
          hasOpenedIncidents,
          setHasOpenedIncidents
        )}
        <div className={styles.field}>
          <Input.Text
            size="lg"
            label="N° do Incidente"
            placeholder="Informe o número do incidente"
            value={incidentNumber}
            onChange={(event) => setIncidentNumber(event.target.value)}
          />
        </div>
        <div className={styles.actions}>
          <Button size="lg" label="Consultar" onClick={consult} />
        </div>
      </div>
      <div className={styles.table}>
        <Table
          loading={isFetching}
          data={tableRows}
          columns={[
            {
              title: "",
              customTitle: (
                <input
                  aria-label="Selecionar todos"
                  type="checkbox"
                  checked={
                    rows.length > 0 &&
                    rows.every((row) =>
                      selected.some((item) => rowKey(item) === rowKey(row))
                    )
                  }
                  onChange={(event) => selectAll(event.target.checked)}
                />
              ),
              columnStyleClasses: styles.selectionColumn,
              cellRender: (row: Sentinel.DelayManagement.Item) => (
                <input
                  aria-label={`Selecionar ${row.processName}`}
                  type="checkbox"
                  checked={selected.some(
                    (item) => rowKey(item) === rowKey(row)
                  )}
                  onChange={(event) =>
                    updateSelection(row, event.target.checked)
                  }
                />
              ),
            },
            {
              title: "Data de atraso",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                formatDate(row.dateOfDelay),
            },
            {
              title: "Canal",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                formatChannels(row.channels),
            },
            { title: "Processo", dataIndex: "processName" },
            {
              title: "Crítico",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                row.isCritical ? "Sim" : "Não",
            },
            {
              title: "Problema",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                renderTruncatedText(row.relatedProblem),
              columnStyleClasses: styles.problemActionColumn,
            },
            {
              title: "Ação",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                renderTruncatedText(row.actionPerformed),
              columnStyleClasses: styles.problemActionColumn,
            },
            {
              title: "Incidente aberto",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                row.hasOpenedIncident ? "Sim" : "Não",
            },
            {
              title: "N° incidente",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                valueOrDash(row.incidentNumber),
            },
            {
              title: "Status",
              cellRender: (row: Sentinel.DelayManagement.Item) => (
                <StatusCell status={row.status} />
              ),
            },
            {
              title: "Sistema ofensor",
              cellRender: (row: Sentinel.DelayManagement.Item) =>
                formatChannels(row.offendingSystems),
            },
            { title: "Início planejado", dataIndex: "plannedExecutionStart" },
            { title: "Fim planejado", dataIndex: "plannedExecutionEnd" },
            { title: "Início real", dataIndex: "realExecutionStart" },
            { title: "Fim real", dataIndex: "realExecutionEnd" },
            { title: "Tolerância", dataIndex: "tolerance" },
            { title: "Atraso", dataIndex: "delay" },
            { title: "Usuário", dataIndex: "user" },
            {
              title: "Ações",
              cellRender: (row: Sentinel.DelayManagement.Item) => (
                <Flex justifyContent="end">
                  <Button
                    label="Histórico"
                    icon="eye-1"
                    buttonStyle="tertiary"
                    onClick={() => openHistory(row)}
                  />
                  <Button
                    label="Editar"
                    icon="edit"
                    buttonStyle="tertiary"
                    onClick={() => openJustification(row)}
                  />
                </Flex>
              ),
            },
          ]}
        />
        <div className={styles.selectionCounter}>
          {selected.length} registro(s) selecionado(s)
        </div>
      </div>
      <div className={styles.pagination}>
        <ClbPagination
          page={data?.currentPage ? data.currentPage + 1 : page + 1}
          pageSize={pageSize}
          totalItems={data?.totalItems || 0}
          onChangePage={(nextPage) => setPage(nextPage - 1)}
          i18n={{
            ariaLabelNextPage: "Próxima página",
            ariaLabelPreviousPage: "Página anterior",
            ariaLabelFirstPage: "Primeira página",
            ariaLabelLastPage: "Última página",
            ariaLabelInput: "Página de {totalItems} itens",
            ariaLabelItemsPerPage: "Itens por página",
            linesPerPage: "itens por página",
            totalPages: "de {totalPages}",
            totalElements: "{firstItem}-{lastItem} de {totalItems}",
            noResults: isLoading ? "Carregando" : "Nenhum resultado encontrado",
          }}
        />
      </div>
      {justificationSelection && (
        <Justification
          event={
            {
              id: justificationSelection.processDefinitionId,
              displayName: justificationSelection.row.processName,
              status: justificationSelection.row.status,
              hasCompleteJustification: !isAwaitingJustification(
                justificationSelection.row.status
              ),
            } as unknown as Sentinel.Timeline.Event
          }
          filterDate={dateFromString(justificationSelection.row.dateOfDelay)}
          isOpen
          onClose={closeJustification}
          onSaved={refreshAfterJustificationSaved}
          sourceFeature="DelayManagement"
        />
      )}
      {historySelection && (
        <JustificationHistory
          row={historySelection.row}
          processDefinitionId={historySelection.processDefinitionId}
          isOpen
          onClose={() => setHistorySelection(undefined)}
        />
      )}
    </main>
  );
};

export default withAuth(DelayManagement);
